import { JSX } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Label, LabelList, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { TotalProviderCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { tickFormatter } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { DashboardTile } from "@app/shared/qes-standards";
import { printNumberAsString } from "@app/shared/waffles";

interface TotalProviderCountProps {
  colorMap: Map<string, string>;
  customTooltip: (
    props: TooltipProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  customYAxisTick?: ({ x, y, payload }: { x: number, y: number, payload: { value: string } }) => JSX.Element;
  data: TotalProviderCountData[]
  loading: boolean;
}

const TotalProviderCount = (props: TotalProviderCountProps): JSX.Element => {
  return (
    <DashboardTile
      loading={props.loading}
      title="Total Provider Count"
      widthClass="col-lg-6"
    >
      {!!props.data?.length ? (
        <div style={{ height: "300px", width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              barCategoryGap="20%"
              data={props.data}
              layout="vertical"
              margin={{
                bottom: 10, // formatting to prevent the "Providers" bottom label from truncating
                left: 0,
                right: 50, // formatting to prevent the number displayed to right of the longest bar from truncating
                top: 20,
              }}
            >
              <CartesianGrid strokeDasharray="1 0" horizontal={false} />
              <XAxis type="number" tickLine={false} tickFormatter={tickFormatter}>
                <Label value="Providers" offset={-10} position="insideBottom" />
              </XAxis>
              <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
              <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} />
              <Bar dataKey="providerCount" fill="#8884d8">
                <LabelList
                  formatter={(providerCount: number) => printNumberAsString(providerCount, "0")}
                  dataKey="providerCount"
                  position="right" // adding the provider count to the side of a bar
                  fill="#000000"
                />
                {props.data.map((entry, index) => (
                  // custom bar chart colors
                  <Cell id={`benchmarking-dashboard__total-provider-count--contract-${entry.contractId}-count-${entry.providerCount}`} key={`cell-${index}`} fill={props.colorMap.get(entry.contractId)} />
                ))
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="copy-text-default font-italic mb-0">
          No results currently displayed. Input search parameters to get results.
        </p>
      )}
    </DashboardTile>
  )
}

export default TotalProviderCount;
